import dayjs from 'dayjs';

export const DATA_TABLE_BLANK_FILTER_VALUE = '__ERP_DATA_TABLE_BLANK__';

export interface DynamicFilter {
  type: 'date' | 'string' | 'number';
  op: string;
  value?: string | number | string[];
  from?: string;
  to?: string;
}

export type DynamicFilters = Record<string, DynamicFilter>;

function parseSingleDate(dateStr: string): dayjs.Dayjs | null {
  const ddmmyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyyMatch) {
    const [, day, month, year] = ddmmyyyyMatch;
    return dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }

  let parsed = dayjs(dateStr, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) {
    parsed = dayjs(dateStr, 'DD-MM-YYYY', true);
  }

  return parsed.isValid() ? parsed : null;
}

export function parseDateRangeInput(input: string): { op: 'range'; from: string; to: string } | null {
  if (!input || !input.trim()) return null;

  const trimmed = input.trim();
  const rangeSeparators = ['-', '&'];

  for (const separator of rangeSeparators) {
    const index = trimmed.indexOf(separator);
    if (index > 0 && index < trimmed.length - separator.length) {
      const fromStr = trimmed.substring(0, index).trim();
      const toStr = trimmed.substring(index + separator.length).trim();

      const fromDate = parseSingleDate(fromStr);
      const toDate = parseSingleDate(toStr);

      if (fromDate && toDate && fromDate.isValid() && toDate.isValid()) {
        return {
          op: 'range',
          from: fromDate.format('YYYY-MM-DD'),
          to: toDate.format('YYYY-MM-DD')
        };
      }
    }
  }

  return null;
}

export function parseDateInput(
  input: string
): { op: string; value: string } | { op: 'range'; from: string; to: string } | null {
  if (!input || !input.trim()) return null;

  const trimmed = input.trim();

  const rangeResult = parseDateRangeInput(trimmed);
  if (rangeResult) {
    return rangeResult;
  }

  const operators = ['<=', '>=', '<', '>', '='];
  let op = '=';
  let dateStr = trimmed;

  for (const operator of operators) {
    if (trimmed.startsWith(operator)) {
      op = operator;
      dateStr = trimmed.substring(operator.length).trim();
      break;
    }
  }

  const parsedDate = parseSingleDate(dateStr);
  if (!parsedDate) return null;

  return {
    op,
    value: parsedDate.format('YYYY-MM-DD')
  };
}

export function parseStringInput(input: string): { op: string; value: string | string[] } | null {
  if (!input || !input.trim()) return null;

  const trimmed = input.trim();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex > 0) {
    const op = trimmed.substring(0, colonIndex).trim();
    const value = trimmed.substring(colonIndex + 1).trim();

    if (value) {
      // For contains operator with semicolon-separated values, return array
      if (op === 'contains' && value.includes(';')) {
        const values = value.split(';').filter(Boolean);
        if (values.length > 1) {
          return { op, value: values };
        }
      }
      return { op, value };
    }
  }

  const operators = ['!=', '==', '='];
  for (const operator of operators) {
    if (trimmed.startsWith(operator)) {
      return {
        op: operator === '==' ? '=' : operator,
        value: trimmed.substring(operator.length).trim()
      };
    }
  }

  return {
    op: 'contains',
    value: trimmed
  };
}

/**
 * Build dynamic_filters object from filter inputs.
 * Fields bắt đầu bằng "ngay_" được coi là date, còn lại là string.
 */
type NumberFilterInput = number | { value: number; op?: string };
type DateRangeInput = { op: string; from: string; to: string };

export function buildDynamicFilters(filters: {
  [key: string]: string | NumberFilterInput | DateRangeInput | undefined;
}): DynamicFilters {
  const dynamicFilters: DynamicFilters = {};

  for (const [field, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;

    if (value === DATA_TABLE_BLANK_FILTER_VALUE) {
      dynamicFilters[field] = {
        type: field.startsWith('ngay_') ? 'date' : 'string',
        op: 'isnull'
      };
      continue;
    }

    if (typeof value === 'number' && !Number.isNaN(value)) {
      dynamicFilters[field] = {
        type: 'number',
        op: '=',
        value
      };
      continue;
    }

    if (typeof value === 'object' && 'op' in value && 'from' in value && 'to' in value) {
      dynamicFilters[field] = {
        type: 'date',
        op: value.op,
        from: value.from,
        to: value.to
      };
      continue;
    }

    if (typeof value === 'object' && 'value' in value) {
      const numericValue = (value as { value: number }).value;
      const numericOp = (value as { op?: string }).op || '=';
      if (typeof numericValue === 'number' && !Number.isNaN(numericValue)) {
        dynamicFilters[field] = {
          type: 'number',
          op: numericOp,
          value: numericValue
        };
      }
      continue;
    }

    if (typeof value !== 'string' || !value.trim()) continue;

    const isDateField = field.startsWith('ngay_');
    if (isDateField) {
      const parsed = parseDateInput(value);
      if (parsed) {
        if (parsed.op === 'range' && 'from' in parsed && 'to' in parsed) {
          dynamicFilters[field] = {
            type: 'date',
            op: 'range',
            from: parsed.from,
            to: parsed.to
          };
        } else if ('value' in parsed) {
          dynamicFilters[field] = {
            type: 'date',
            op: parsed.op,
            value: parsed.value
          };
        }
      }
    } else {
      const parsed = parseStringInput(value);
      if (parsed) {
        dynamicFilters[field] = {
          type: 'string',
          op: parsed.op,
          value: parsed.value
        };
      }
    }
  }

  return dynamicFilters;
}
